import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CircularProgress,
  TablePagination,
  Button,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { getUploadedForm } from "../../../store/actions/uiActions/myPermitsActions";
import { openBlankTab, setNewTabLocation } from "../../../utils/openNewTab";

const JaxDocs = ({
  selectedDocId,
  setSelectedDocId,
  selectedFile,
  setSelectedFile,
}) => {
  const { jaxDocs, viewDocLoading } = useSelector(
    (state) => state.myPermitsComponent.citySite,
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const dispatch = useDispatch();

  const filteredDocs = useMemo(() => {
    return jaxDocs?.filter((doc) => doc.required || doc.uploaded) || [];
  }, [jaxDocs]);

  const docStats = useMemo(() => {
    const requiredCount = jaxDocs?.filter((doc) => doc.required)?.length || 0;
    const uploadedCount =
      jaxDocs?.filter((doc) => doc.required && doc.uploaded)?.length || 0;
    return { uploadedCount, requiredCount };
  }, [jaxDocs]);

  const handleSelectChange = (e) => {
    setSelectedDocId(e.target.value);
  };

  const handleSeeForm = async (url) => {
    const newTab = openBlankTab();
    const blob = await dispatch(getUploadedForm(url));
    setNewTabLocation(newTab, blob);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedDocs = filteredDocs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  useEffect(() => {
    const validIds = jaxDocs
      ?.filter((doc) => !doc.uploaded)
      .map((doc) => doc.docTypeId);
    if (!validIds.includes(selectedDocId)) {
      setSelectedDocId("");
    }
  }, [selectedDocId, setSelectedDocId, jaxDocs]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="h6" gutterBottom>
        City Required / Uploaded Documents -{" "}
        <span
          style={{
            color:
              docStats.uploadedCount < docStats.requiredCount
                ? "#F04438"
                : "#4CAF50",
          }}
        >
          {docStats.uploadedCount}/{docStats.requiredCount}
        </span>
      </Typography>

      {/* Scrollable Table */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell align="center">Required</TableCell>
                <TableCell align="center">Uploaded</TableCell>
                <TableCell align="center">Size (MB)</TableCell>
                <TableCell align="center">File Name</TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDocs.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.label}</TableCell>
                  <TableCell align="center">
                    {doc.required ? "Yes" : "No"}
                  </TableCell>
                  <TableCell align="center">
                    {doc.uploaded ? "Yes" : "No"}
                  </TableCell>
                  <TableCell align="center">
                    {doc.sizeMB != null ? `${doc.sizeMB.toFixed(2)} MB` : "-"}
                  </TableCell>
                  <TableCell align="center">{doc.fileName || "-"}</TableCell>
                  <TableCell align="center">
                    {doc.uploaded ? (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          viewDocLoading ? null : handleSeeForm(doc.url)
                        }
                        title="View in JaxEPICS"
                      >
                        {String(viewDocLoading) === String(doc?.documentId) ? (
                          <CircularProgress size={18} />
                        ) : (
                          <LaunchIcon sx={{ fontSize: "20px" }} />
                        )}
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
          component="div"
          count={filteredDocs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>

      {/* Upload Area */}
      <Box sx={{ pt: 2, borderTop: "1px solid #ccc" }}>
        <Typography variant="h6" mb={1}>
          Upload Document
        </Typography>
        <FormControl fullWidth size="small" variant="filled">
          <Select
            value={
              jaxDocs.some((d) => !d.uploaded && d.docTypeId === selectedDocId)
                ? selectedDocId
                : ""
            }
            onChange={handleSelectChange}
            displayEmpty
            renderValue={(value) => {
              const found = jaxDocs.find((doc) => doc.docTypeId === value);
              return found ? found.label : "Select Document to Upload";
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  marginTop: "1.5px",
                  maxHeight: 220,
                  width: "auto",
                  maxWidth: "100%",
                },
              },
            }}
          >
            {jaxDocs
              ?.filter((doc) => !doc.uploaded)
              .map((doc) => (
                <MenuItem key={doc.id} value={doc.docTypeId}>
                  {doc.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {selectedFile && (
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            <strong>Selected File:</strong> {selectedFile.name}
          </Typography>
        )}

        {selectedDocId && (
          <Box
            gap={1}
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() =>
                selectedFile ? setSelectedFile(null) : setSelectedDocId("")
              }
              size="small"
              variant="text"
              color="error"
            >
              {selectedFile ? "Change File" : "Clear"}
            </Button>

            <>
              <input
                type="file"
                hidden
                id="upload-doc-input"
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
              <label htmlFor="upload-doc-input">
                <Button
                  disabled={!!selectedFile}
                  component="span"
                  size="small"
                  variant="contained"
                >
                  Select File
                </Button>
              </label>
            </>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default JaxDocs;
